import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Mail, Phone, MapPin, MessageCircle, Send, CheckCircle2, AlertCircle, Info, Clock, Shield } from 'lucide-react';
import SEO from './SEO';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { submitContact } from '../services/api';

// Shared layout wrapper for all corporate pages.
const Page = ({ title, description, lastUpdated, children }) => (
  <div className="min-h-[60vh] bg-[#fafaf7]">
    <SEO title={title} description={description} />
    <div className="bg-white border-b border-gray-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#3D4A26] mb-3">
          Kurumsal
        </div>
        <h1
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B1B] leading-tight"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          {title}
        </h1>
        {lastUpdated && (
          <p className="text-sm text-gray-500 mt-3">Son güncelleme: {lastUpdated}</p>
        )}
      </div>
    </div>
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div
        className="prose prose-neutral max-w-none text-[15px] leading-relaxed text-[#2a2a2a]"
        style={{ fontFamily: 'DM Sans, sans-serif' }}
      >
        {children}
      </div>
      <div className="mt-12 pt-8 border-t border-gray-200 text-sm text-gray-500">
        <Link to="/" className="text-[#3D4A26] hover:underline">← Anasayfaya dön</Link>
      </div>
    </article>
  </div>
);

const H2 = ({ children }) => (
  <h2
    className="text-xl md:text-2xl font-bold text-[#1B1B1B] mt-8 mb-3"
    style={{ fontFamily: 'DM Sans, sans-serif' }}
  >
    {children}
  </h2>
);

const P = ({ children }) => <p className="mb-4 text-[#2a2a2a]">{children}</p>;

const UL = ({ children }) => (
  <ul className="list-disc pl-6 mb-4 space-y-1.5 text-[#2a2a2a]">{children}</ul>
);

// ─────────────────────────────────────────────────────────
//  Contact form (used inside ContactPage)
// ─────────────────────────────────────────────────────────
const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status.state === 'sending') return;
    setStatus({ state: 'sending', msg: '' });
    try {
      await submitContact(form);
      setStatus({ state: 'success', msg: 'Mesajınız iletildi. En kısa sürede dönüş yapacağız.' });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setStatus({
        state: 'error',
        msg: err.response?.data?.detail || 'Bir hata oluştu. Lütfen tekrar deneyin.',
      });
    }
  };

  const inputCls =
    'w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] focus:border-transparent transition-shadow';

  return (
    <form onSubmit={submit} className="not-prose" data-testid="contact-form" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Ad Soyad *</label>
          <input
            required
            value={form.name}
            onChange={update('name')}
            placeholder="Adınız soyadınız"
            className={inputCls}
            data-testid="contact-name"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">E-posta *</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={update('email')}
            placeholder="ornek@email.com"
            className={inputCls}
            data-testid="contact-email"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Telefon</label>
          <input
            value={form.phone}
            onChange={update('phone')}
            placeholder="0 5XX XXX XX XX (opsiyonel)"
            className={inputCls}
            data-testid="contact-phone"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Konu</label>
          <input
            value={form.subject}
            onChange={update('subject')}
            placeholder="Rezervasyon, tur sorusu, ..."
            className={inputCls}
            data-testid="contact-subject"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs font-semibold text-[#1B1B1B] mb-1.5">Mesajınız *</label>
        <textarea
          required
          rows={6}
          value={form.message}
          onChange={update('message')}
          placeholder="Sorunuzu, talebinizi ya da geri bildiriminizi yazın..."
          className={`${inputCls} resize-none`}
          data-testid="contact-message"
        />
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status.state === 'sending'}
          data-testid="contact-submit"
          className="inline-flex items-center justify-center gap-2 bg-[#3D4A26] text-white font-bold px-7 py-3 rounded-full text-sm hover:bg-[#2d3a1a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status.state === 'sending' ? 'Gönderiliyor...' : (<>
            <Send size={15} /> Mesajı Gönder
          </>)}
        </button>

        {status.state === 'success' && (
          <p className="flex items-center gap-2 text-sm text-green-700" data-testid="contact-success">
            <CheckCircle2 size={16} /> {status.msg}
          </p>
        )}
        {status.state === 'error' && (
          <p className="flex items-center gap-2 text-sm text-red-600" data-testid="contact-error">
            <AlertCircle size={16} /> {status.msg}
          </p>
        )}
      </div>

      <p className="mt-4 text-xs text-gray-500">
        Formu göndererek{' '}
        <Link to="/kvkk" className="text-[#3D4A26] underline">KVKK Aydınlatma Metni</Link>
        {' '}kapsamında verilerinizin işlenmesini kabul etmiş olursunuz.
      </p>
    </form>
  );
};

// ─────────────────────────────────────────────────────────
//  1. Bize Ulaşın
// ─────────────────────────────────────────────────────────
export const ContactPage = () => {
  const s = useSiteSettings();
  const telDigits = (s.phone || '').replace(/\D/g, '');
  return (
  <Page
    title="Bize Ulaşın"
    description="Camption ekibi ile iletişime geçin. Telefon, e-posta ve WhatsApp üzerinden 7/24 destek."
  >
    <P>
      Tur seçimi, rezervasyon, ekipman veya rotalarımız hakkındaki her türlü
      sorunuz için bize aşağıdaki kanallardan ulaşabilirsiniz. Mesai
      saatlerimiz dışında WhatsApp mesajlarınıza ilk iş günü içinde dönüş
      sağlanır.
    </P>

    {/* Contact channels */}
    <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
      <a href={`tel:+${telDigits}`} className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-[#C8D843] transition-colors">
        <Phone size={20} className="text-[#3D4A26] mt-0.5" />
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Telefon</p>
          <p className="font-semibold text-[#1B1B1B]">{s.phone}</p>
          <p className="text-xs text-gray-500 mt-1">Hafta içi {s.hoursWeekday}</p>
        </div>
      </a>
      <a href={`https://wa.me/${s.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-[#C8D843] transition-colors">
        <MessageCircle size={20} className="text-[#3D4A26] mt-0.5" />
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">WhatsApp</p>
          <p className="font-semibold text-[#1B1B1B]">{s.phone}</p>
          <p className="text-xs text-gray-500 mt-1">7 gün hızlı yanıt</p>
        </div>
      </a>
      <a href={`mailto:${s.email}`} className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-[#C8D843] transition-colors">
        <Mail size={20} className="text-[#3D4A26] mt-0.5" />
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">E-posta</p>
          <p className="font-semibold text-[#1B1B1B]">{s.email}</p>
          <p className="text-xs text-gray-500 mt-1">24 saat içinde dönüş</p>
        </div>
      </a>
      <div className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5">
        <MapPin size={20} className="text-[#3D4A26] mt-0.5" />
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Adres</p>
          <p className="font-semibold text-[#1B1B1B]">Camption Outdoor Turizm</p>
          <p className="text-xs text-gray-500 mt-1">{s.address}</p>
        </div>
      </div>
    </div>

    {/* Info box */}
    <div className="not-prose my-10 bg-[#f4f7e6] border border-[#C8D843]/40 rounded-2xl p-6 md:p-7" data-testid="contact-info-box">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#C8D843] flex items-center justify-center flex-shrink-0">
          <Info size={18} className="text-[#1B1B1B]" />
        </div>
        <div>
          <h3 className="font-bold text-[#1B1B1B] text-base md:text-lg mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Mesaj göndermeden önce
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed">
            Cevabını formu doldurmadan da bulabileceğiniz konular için aşağıdakileri inceleyebilirsiniz. Yine de bize yazmaktan çekinmeyin, en geç bir iş günü içinde dönüş sağlıyoruz.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <div className="flex items-start gap-2.5 bg-white rounded-lg p-3 border border-gray-100">
          <Clock size={16} className="text-[#3D4A26] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-bold text-[#1B1B1B] mb-0.5">Yanıt süresi</p>
            <p className="text-[11px] text-gray-600 leading-snug">İş günleri 4 saat içinde dönüyoruz.</p>
          </div>
        </div>
        <Link to="/yardim-ve-sss" className="flex items-start gap-2.5 bg-white rounded-lg p-3 border border-gray-100 hover:border-[#C8D843] transition-colors">
          <MessageCircle size={16} className="text-[#3D4A26] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-bold text-[#1B1B1B] mb-0.5">SSS</p>
            <p className="text-[11px] text-gray-600 leading-snug">Rezervasyon ve tur hakkında en sık sorulanlar.</p>
          </div>
        </Link>
        <Link to="/iptal-politikasi" className="flex items-start gap-2.5 bg-white rounded-lg p-3 border border-gray-100 hover:border-[#C8D843] transition-colors">
          <Shield size={16} className="text-[#3D4A26] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-bold text-[#1B1B1B] mb-0.5">İptal politikası</p>
            <p className="text-[11px] text-gray-600 leading-snug">İade ve değişiklik koşullarımız.</p>
          </div>
        </Link>
      </div>
    </div>

    <H2>Bize Yazın</H2>
    <P>
      Aşağıdaki formu doldurarak ekibimize doğrudan ulaşabilirsiniz. Tüm
      mesajlar ekibimiz tarafından okunur ve en geç bir iş günü içinde
      cevaplanır.
    </P>
    <ContactForm />

    <H2>Mesai Saatleri</H2>
    <UL>
      <li>Pazartesi - Cuma: {s.hoursWeekday}</li>
      <li>Cumartesi: {s.hoursSaturday}</li>
      <li>Pazar: {s.hoursSunday}</li>
    </UL>
  </Page>
  );
};

// ─────────────────────────────────────────────────────────
//  2. Yardım ve SSS
// ─────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  {
    q: 'Rezervasyon nasıl yapılır?',
    a: 'Tur sayfasından dilediğiniz tarihi seçip "Rezervasyon Yap" düğmesine tıklayarak başlayabilirsiniz. Adınız, soyadınız ve iletişim bilgileriniz alındıktan sonra ekibimiz sizinle 24 saat içinde irtibata geçer ve ödeme detaylarını paylaşır.',
  },
  {
    q: 'Hangi rotada hangi seviye gerekir?',
    a: 'Her turun detay sayfasında "Seviye" göstergesi bulunur. Başlangıç seviyesindeki turlar günlük 4-6 saat, ileri seviye turlar günlük 7-9 saat yürüyüş içerir. Düzenli yürüyüş yapan herkes orta seviyeye uygundur.',
  },
  {
    q: 'Tur ücretine neler dahildir?',
    a: 'Tüm turlarımıza otel/bungalov konaklaması, transfer, profesyonel rehberlik, kahvaltı ve akşam yemekleri ile teknik destek (acil durum çantası, ilkyardım) dahildir. Öğle yemekleri ve kişisel harcamalar dahil değildir.',
  },
  {
    q: 'Tek başıma katılabilir miyim?',
    a: 'Evet. Turlarımızın yaklaşık %40\'ı tek başına gelen katılımcılardan oluşur. Konaklamada eşleştirme yapılır; tek kişilik oda için ekstra ücret ödeyerek özel odada kalabilirsiniz.',
  },
  {
    q: 'Hangi ekipmanları getirmem gerekir?',
    a: 'Turdan önce size detaylı bir "ekipman listesi" gönderilir. Temel olarak iyi bir trekking ayakkabısı, sırt çantası (25-35L), su matarası, mevsime uygun katmanlı kıyafetler ve yağmurluk yeterlidir.',
  },
  {
    q: 'Hava şartları kötü olursa tur iptal olur mu?',
    a: 'Güvenliğiniz önceliğimizdir. Olumsuz hava şartlarında rota değişikliği yapabilir veya kısa süreli erteleme uygulayabiliriz. Tam iptal yalnızca güvenlik riski varsa söz konusudur; bu durumda ücretinizin tamamı iade edilir veya başka bir tara aktarılır.',
  },
  {
    q: 'İade ve iptal koşulları nelerdir?',
    a: <>Detaylı bilgi için <Link to="/iptal-politikasi" className="text-[#3D4A26] underline">İptal Politikası</Link> sayfamıza göz atabilirsiniz.</>,
  },
  {
    q: 'Kişisel verilerim nasıl korunuyor?',
    a: <>Tüm verileriniz KVKK uyumlu işlenir. Detaylı bilgi için <Link to="/kvkk" className="text-[#3D4A26] underline">KVKK Aydınlatma Metni</Link>.</>,
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${q.slice(0, 20)}`}
        className="w-full flex items-center justify-between py-5 text-left hover:text-[#3D4A26] transition-colors"
      >
        <span className="font-semibold text-[#1B1B1B] pr-4">{q}</span>
        <ChevronDown
          size={20}
          className={`flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180 text-[#3D4A26]' : ''}`}
        />
      </button>
      {open && <div className="pb-5 text-[#2a2a2a] leading-relaxed">{a}</div>}
    </div>
  );
};

export const FaqPage = () => (
  <Page
    title="Yardım ve SSS"
    description="Camption tur ve rezervasyon süreçleri hakkında sıkça sorulan sorular ve yanıtları."
  >
    <P>
      Aşağıda en sık karşılaştığımız soruları derledik. Cevabını bulamadığınız
      bir konu olursa <Link to="/bize-ulasin" className="text-[#3D4A26] underline">Bize Ulaşın</Link> sayfasından bizimle iletişime
      geçmekten çekinmeyin.
    </P>
    <div className="not-prose mt-6 bg-white rounded-xl border border-gray-200 px-6">
      {FAQ_ITEMS.map((item) => (
        <FAQItem key={item.q} q={item.q} a={item.a} />
      ))}
    </div>
  </Page>
);

// ─────────────────────────────────────────────────────────
//  3. Mesafeli Satış Sözleşmesi
// ─────────────────────────────────────────────────────────
export const SalesAgreementPage = () => (
  <Page
    title="Mesafeli Satış Sözleşmesi"
    description="Camption mesafeli satış sözleşmesi — Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği kapsamında hazırlanmıştır."
    lastUpdated="01 Şubat 2026"
  >
    <H2>1. Taraflar</H2>
    <P>
      Bir tarafta tur hizmetini sağlayan <strong>Camption Outdoor Turizm</strong>
      (kısaca "SATICI") ile diğer tarafta web sitesi üzerinden rezervasyon
      gerçekleştiren <strong>ALICI</strong> arasında, aşağıdaki şartlar
      dahilinde işbu mesafeli satış sözleşmesi akdedilmiştir.
    </P>

    <H2>2. Konu</H2>
    <P>
      İşbu sözleşmenin konusu, ALICI\'nın SATICI\'ya ait camption.com web
      sitesinden elektronik ortamda rezervasyonunu yaptığı, aşağıda
      nitelikleri ve satış bedeli belirtilen tur paketinin satışı ve
      ifasıyla ilgili olarak 6502 sayılı Tüketicinin Korunması Hakkında
      Kanun ile Mesafeli Sözleşmeler Yönetmeliği hükümleri gereğince
      tarafların hak ve yükümlülüklerinin tespitidir.
    </P>

    <H2>3. Hizmetin Temel Nitelikleri</H2>
    <P>
      Tur paketi; konaklama, transfer, profesyonel rehberlik hizmeti,
      sözleşmede belirtilen yemekler ve sigorta kalemlerini içerir. Tur
      süresi, rotası ve katılımcı kapasitesi ilgili tur sayfasında detaylı
      olarak belirtilmiştir.
    </P>

    <H2>4. Ödeme Şartları</H2>
    <UL>
      <li>Rezervasyon onayı için tur ücretinin %30\'u kapora olarak alınır.</li>
      <li>Kalan tutar tur başlangıcından en geç 15 gün önce ödenir.</li>
      <li>Ödeme kredi kartı, havale/EFT veya online ödeme kanallarıyla yapılır.</li>
      <li>Tüm fiyatlar KDV dahildir ve Türk Lirası (₺) cinsindendir.</li>
    </UL>

    <H2>5. Cayma Hakkı</H2>
    <P>
      Mesafeli Sözleşmeler Yönetmeliği m. 15/g uyarınca, belirli bir tarihte
      veya dönemde ifa edilmesi gereken konaklama, eşya taşıma, araba kiralama,
      yiyecek-içecek tedariki ve eğlence veya dinlenme amacıyla yapılan
      boş zamanın değerlendirilmesine ilişkin sözleşmeler kapsamındaki tur
      hizmetlerinde <strong>cayma hakkı kullanılamaz</strong>. Ancak ALICI,
      <Link to="/iptal-politikasi" className="text-[#3D4A26] underline"> İptal Politikası</Link>
      \'nda belirtilen kademeli iade haklarından faydalanır.
    </P>

    <H2>6. Tarafların Yükümlülükleri</H2>
    <P>
      SATICI; sözleşme konusu hizmetin sağlam, eksiksiz, sözleşmeye uygun
      ve varsa garanti belgeleri ile teslim edilmesinden sorumludur. ALICI
      ise tur sırasında rehberlerin güvenlik talimatlarına uymak ve doğru
      iletişim bilgileri sağlamakla yükümlüdür.
    </P>

    <H2>7. Mücbir Sebepler</H2>
    <P>
      Doğal afet, salgın hastalık, savaş, grev, hükümet kararları gibi
      önceden öngörülemeyen ve tarafların kontrolü dışındaki olaylar
      mücbir sebep olarak değerlendirilir ve bu durumda tur ertelenebilir
      veya alternatif tarihler önerilebilir.
    </P>

    <H2>8. Yetkili Mahkeme</H2>
    <P>
      İşbu sözleşmenin uygulanmasından doğacak uyuşmazlıklarda Gümrük ve
      Ticaret Bakanlığı tarafından yıllık olarak belirlenen parasal sınırlar
      dahilinde Tüketici Hakem Heyetleri, bu sınırı aşan durumlarda Antalya
      Tüketici Mahkemeleri yetkilidir.
    </P>

    <H2>9. Yürürlük</H2>
    <P>
      ALICI, sitedeki rezervasyon formunu doldurup ödemeyi tamamladığında
      işbu sözleşmenin tüm hükümlerini kabul etmiş sayılır. Sözleşme
      elektronik ortamda kurulmuş olup tarafların ayrıca imzalamasına
      gerek yoktur.
    </P>
  </Page>
);

// ─────────────────────────────────────────────────────────
//  4. İptal Politikası
// ─────────────────────────────────────────────────────────
export const CancellationPage = () => (
  <Page
    title="İptal Politikası"
    description="Camption tur iptal koşulları, iade kademeleri ve mücbir sebep durumları."
    lastUpdated="01 Şubat 2026"
  >
    <P>
      Camption olarak esnek ve şeffaf bir iptal politikası uyguluyoruz.
      Aşağıdaki şartlar tüm rezervasyonlar için geçerlidir. Daha avantajlı
      özel koşullar tur sayfasında belirtilmiş ise, ilgili koşullar
      uygulanır.
    </P>

    <H2>Kademeli İade Tablosu</H2>
    <div className="not-prose my-6 overflow-hidden rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead className="bg-[#3D4A26] text-white">
          <tr>
            <th className="text-left px-4 py-3 font-semibold">İptal Zamanı</th>
            <th className="text-left px-4 py-3 font-semibold">İade Oranı</th>
          </tr>
        </thead>
        <tbody className="bg-white">
          <tr className="border-t border-gray-100">
            <td className="px-4 py-3">Tur başlangıcından <strong>30+ gün</strong> önce</td>
            <td className="px-4 py-3 font-semibold text-green-700">%100 iade</td>
          </tr>
          <tr className="border-t border-gray-100 bg-gray-50/50">
            <td className="px-4 py-3">Tur başlangıcından <strong>15-29 gün</strong> önce</td>
            <td className="px-4 py-3 font-semibold text-[#3D4A26]">%70 iade</td>
          </tr>
          <tr className="border-t border-gray-100">
            <td className="px-4 py-3">Tur başlangıcından <strong>7-14 gün</strong> önce</td>
            <td className="px-4 py-3 font-semibold text-orange-600">%40 iade</td>
          </tr>
          <tr className="border-t border-gray-100 bg-gray-50/50">
            <td className="px-4 py-3">Tur başlangıcından <strong>0-6 gün</strong> önce</td>
            <td className="px-4 py-3 font-semibold text-red-600">İade yok</td>
          </tr>
        </tbody>
      </table>
    </div>

    <H2>Tur Tarihinin Değiştirilmesi</H2>
    <P>
      Tarih değişikliği talepleri tur başlangıcından en az 15 gün önce
      bildirildiğinde <strong>ücretsiz</strong> olarak yapılır. Daha sonraki
      değişiklik talepleri için kalan kontenjana göre değerlendirme yapılır.
    </P>

    <H2>Camption Tarafından İptal</H2>
    <P>
      Minimum katılımcı sayısına ulaşılamaması, olumsuz hava koşulları veya
      mücbir sebepler nedeniyle Camption turu iptal etmek zorunda kalırsa,
      ödenmiş tüm tutar 14 iş günü içinde kayıtsız şartsız iade edilir veya
      katılımcının onayıyla başka bir tara aktarılır.
    </P>

    <H2>Mücbir Sebepler</H2>
    <P>
      Aşağıdaki durumlar mücbir sebep olarak kabul edilir ve iptal halinde
      %100 iade hakkı doğurur:
    </P>
    <UL>
      <li>Resmi makamlarca açıklanan salgın/karantina kararları</li>
      <li>Doğal afet, sel, deprem, orman yangını</li>
      <li>Devlet kararıyla bölgeye giriş yasağı</li>
      <li>Belgelenmiş ağır sağlık problemi (hastane raporu)</li>
    </UL>

    <H2>İade Süreci</H2>
    <UL>
      <li>İade talebi e-posta veya WhatsApp ile yazılı iletilmelidir.</li>
      <li>Ödemenin yapıldığı kanala iade edilir (kredi kartı / banka hesabı).</li>
      <li>İşlem süresi 7-14 iş günüdür (banka süreçlerine bağlı).</li>
    </UL>
  </Page>
);

// ─────────────────────────────────────────────────────────
//  5. KVKK Aydınlatma Metni
// ─────────────────────────────────────────────────────────
export const KvkkPage = () => {
  const s = useSiteSettings();
  return (
  <Page
    title="KVKK Aydınlatma Metni"
    description="6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında Camption tarafından hazırlanan aydınlatma metni."
    lastUpdated="01 Şubat 2026"
  >
    <H2>1. Veri Sorumlusu</H2>
    <P>
      6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca,
      kişisel verileriniz; veri sorumlusu sıfatıyla{' '}
      <strong>Camption Outdoor Turizm</strong> tarafından aşağıda açıklanan
      kapsamda işlenebilecektir.
    </P>

    <H2>2. İşlenen Kişisel Veriler</H2>
    <UL>
      <li><strong>Kimlik Bilgileri:</strong> Ad, soyad, T.C. kimlik numarası, doğum tarihi.</li>
      <li><strong>İletişim Bilgileri:</strong> Telefon numarası, e-posta adresi, açık adres.</li>
      <li><strong>Sağlık Bilgileri:</strong> Alerji, kullanılan ilaç, acil durum kontağı (yalnızca güvenlik amaçlı).</li>
      <li><strong>Finansal Bilgiler:</strong> Ödeme dekontu, fatura bilgileri (kart numarası saklanmaz).</li>
      <li><strong>İşlem Güvenliği:</strong> IP adresi, çerez verileri, oturum kayıtları.</li>
    </UL>

    <H2>3. İşleme Amaçları</H2>
    <UL>
      <li>Rezervasyon sürecinin yürütülmesi ve tur hizmetinin sunulması</li>
      <li>Sözleşmesel ve yasal yükümlülüklerin yerine getirilmesi</li>
      <li>Müşteri memnuniyeti süreçlerinin yönetilmesi</li>
      <li>Acil durum ve güvenlik tedbirlerinin alınması</li>
      <li>Pazarlama ve tanıtım faaliyetleri (yalnızca açık rızanız varsa)</li>
      <li>Yasal raporlama ve denetim faaliyetleri</li>
    </UL>

    <H2>4. Aktarılan Kişiler</H2>
    <P>
      Kişisel verileriniz; yalnızca yasal yükümlülüklerin gereği veya hizmetin
      sunumu için zorunlu olduğu durumlarda; resmi kurum ve kuruluşlara,
      sigorta şirketlerine, ödeme altyapısı sağlayıcılarına ve iş ortaklarımıza
      KVKK m. 8 ve 9 hükümlerine uygun şekilde aktarılabilir.
    </P>

    <H2>5. Toplama Yöntemi ve Hukuki Sebep</H2>
    <P>
      Kişisel verileriniz; web sitemiz üzerinden doldurulan formlar,
      e-posta, telefon, WhatsApp ve yüz yüze iletişim kanalları aracılığıyla
      toplanır. Hukuki sebep olarak KVKK m. 5/2 (c)-(ç)-(e)-(f) ve m. 6/2 (a)
      bendlerine dayanılmaktadır.
    </P>

    <H2>6. Haklarınız (KVKK m. 11)</H2>
    <UL>
      <li>Kişisel verinizin işlenip işlenmediğini öğrenme</li>
      <li>İşlenmişse buna ilişkin bilgi talep etme</li>
      <li>İşlenme amacını ve amaca uygun kullanılıp kullanılmadığını öğrenme</li>
      <li>Yurt içinde / dışında aktarıldığı üçüncü kişileri bilme</li>
      <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme</li>
      <li>Silinmesini veya yok edilmesini isteme</li>
      <li>İşlemin sınırlandırılmasını talep etme</li>
      <li>Aktarıldığı üçüncü kişilere düzeltme/silme bildirilmesini isteme</li>
      <li>Otomatik sistemler aracılığıyla analiz edilmesi sonucu aleyhine sonuç doğmasına itiraz etme</li>
      <li>Zarara uğraması halinde tazminini talep etme</li>
    </UL>

    <H2>7. Başvuru Yöntemi</H2>
    <P>
      Yukarıdaki haklarınızı kullanmak için{' '}
      <a href={`mailto:${s.email}`} className="text-[#3D4A26] underline">{s.email}</a>{' '}
      adresine kimliğinizi tespit edici belgeler ve kullanmak istediğiniz
      hakkı içeren talebinizle başvurabilirsiniz. Talebiniz en geç 30 gün
      içinde sonuçlandırılır.
    </P>

    <H2>8. Veri Saklama Süresi</H2>
    <P>
      Kişisel verileriniz; tur hizmetinin tamamlanmasının ardından yasal
      saklama süreleri (vergi mevzuatı, ticaret hukuku) boyunca tutulur ve
      sürenin sonunda imha edilir. Pazarlama amaçlı veriler ise rızanızı
      geri çekene kadar saklanır.
    </P>
  </Page>
  );
};
